import type { Message } from '@/types/chat';
import type { DifyHistoryMessage } from '@/types/chat';
import type {
  IMessageHydrationAdapter,
  CachedMessage,
  CachedMessageV3,
  MessageUpdate
} from './types';
import {
  extractCitationsFromHistory,
  mergeCardBlocksByPosition,
  flattenCardsFromBlocks,
  deepEqual
} from './utils';
import { extractStructuredAssistantResponse } from '@/lib/assistantStructuredCards';

type DifyMessageFile = {
  id?: string;
  belongs_to?: string;
};

type DifyMessageFileWithId = {
  id: string;
  belongs_to?: string;
};

/**
 * Version 3 message hydration adapter.
 * Supports schema migration from V1 (no cards) and V2 (cards but no cardBlocks).
 */
export class MessageHydrationAdapterV3 implements IMessageHydrationAdapter {
  readonly version = 3;

  constructor(private readonly userId: string) {}

  /**
   * Deserialize message from localStorage cache.
   * Auto-migrates V1 and V2 formats to V3.
   * Never restores agentActivity or isThinking (ephemeral state).
   */
  fromCache(cached: unknown): Message | null {
    if (!cached || typeof cached !== 'object') {
      return null;
    }

    const raw = cached as Partial<CachedMessage>;

    // Validate required fields
    if (!raw.id || !raw.role || raw.content === undefined || !raw.timestamp) {
      return null;
    }

    // Auto-migrate if older version
    const version = raw.version ?? 1;
    if (version < 3) {
      return this.migrate(raw as CachedMessage);
    }

    // Parse V3 format
    const v3 = raw as CachedMessageV3;
    return {
      id: v3.id,
      role: v3.role as 'user' | 'assistant',
      content: v3.content,
      timestamp: new Date(v3.timestamp),
      fileIds: v3.fileIds,
      difyMessageId: v3.difyMessageId,
      replyToMessageId: v3.replyToMessageId,
      citations: v3.citations,
      cards: v3.cards,
      cardBlocks: v3.cardBlocks,
      feedback: this.loadFeedbackFromCache(v3.id),
      // Explicitly never restore agentActivity or isThinking
    };
  }

  /**
   * Deserialize messages from Dify history API.
   * Returns array: [user_message?, assistant_message?]
   */
  fromHistory(msg: DifyHistoryMessage): Message[] {
    const messages: Message[] = [];
    const userMsgId = `${msg.id}-user`;
    const assistantMsgId = `${msg.id}-assistant`;

    // Extract user message
    if (msg.query) {
      const fileIds = this.extractMessageFileIds(msg, 'user');

      messages.push({
        id: userMsgId,
        role: 'user',
        content: msg.query,
        timestamp: new Date(msg.created_at * 1000),
        fileIds: fileIds.length > 0 ? fileIds : undefined,
        difyMessageId: msg.id,
        feedback: this.loadFeedbackFromCache(userMsgId),
      });
    }

    // Extract assistant message
    if (msg.answer) {
      const structured = extractStructuredAssistantResponse(msg.answer, { final: true });
      const citations = extractCitationsFromHistory(msg, assistantMsgId);
      const fileIds = this.extractMessageFileIds(msg, 'assistant');

      messages.push({
        id: assistantMsgId,
        role: 'assistant',
        content: structured.content,
        timestamp: new Date(msg.created_at * 1000),
        fileIds: fileIds.length > 0 ? fileIds : undefined,
        difyMessageId: msg.id,
        citations: citations.length > 0 ? citations : undefined,
        hasCitations: citations.length > 0,
        cards: structured.cards.length > 0 ? structured.cards : undefined,
        cardBlocks: structured.cardBlocks.length > 0 ? structured.cardBlocks : undefined,
        feedback: this.loadFeedbackFromCache(assistantMsgId),
      });
    }

    return messages;
  }

  /**
   * Serialize message for localStorage cache.
   * Strips ephemeral fields (agentActivity, isThinking) and feedback.
   * Feedback is persisted separately in feedback-${messageId}-${userId} keys.
   */
  toCache(message: Message): CachedMessageV3 {
    return {
      version: 3,
      id: message.id,
      role: message.role,
      content: message.content,
      timestamp: message.timestamp.toISOString(),
      fileIds: message.fileIds,
      difyMessageId: message.difyMessageId,
      replyToMessageId: message.replyToMessageId,
      citations: message.citations,
      cards: message.cards,
      cardBlocks: message.cardBlocks,
    };
  }

  /**
   * Migrate older cached message to V3 format.
   */
  migrate(message: CachedMessage): Message {
    const version = message.version ?? 1;
    let migrated: CachedMessage = message;

    if (version === 1) {
      // V1 -> V2: Add empty cards array
      migrated = {
        ...migrated,
        version: 2,
        cards: []
      };
    }

    if (version <= 2) {
      // V2 -> V3: Add empty cardBlocks array
      migrated = {
        ...migrated,
        version: 3,
        cardBlocks: []
      };
    }

    const result = this.fromCache(migrated);
    if (!result) {
      throw new Error(`Failed to migrate message to version ${this.version}`);
    }
    return result;
  }

  /**
   * Check structural equality for deduplication.
   * Compares role, content (trimmed), cards, and cardBlocks.
   * Ignores id, timestamp, and other metadata.
   */
  equals(a: Message, b: Message): boolean {
    if (a.role !== b.role) return false;
    if (a.content.trim() !== b.content.trim()) return false;

    // Use deep structural comparison for cards/cardBlocks
    if (!deepEqual(a.cards, b.cards)) return false;
    if (!deepEqual(a.cardBlocks, b.cardBlocks)) return false;

    return true;
  }

  /**
   * Merge streaming update into existing message.
   * Handles card block merging and flattening.
   */
  merge(existing: Message, update: MessageUpdate): Message {
    let mergedCardBlocks = existing.cardBlocks;

    // If update includes cardBlocks, merge them by position
    if (update.cardBlocks && Array.isArray(update.cardBlocks)) {
      for (const incomingBlock of update.cardBlocks) {
        mergedCardBlocks = mergeCardBlocksByPosition(mergedCardBlocks, incomingBlock);
      }
    }

    // Flatten cardBlocks to cards for backward compatibility
    const mergedCards = flattenCardsFromBlocks(mergedCardBlocks);

    return {
      ...existing,
      ...update,
      cardBlocks: mergedCardBlocks,
      cards: mergedCards,
    };
  }

  /**
   * Load feedback from separate localStorage key.
   * Feedback is persisted separately to avoid cache bloat.
   */
  private loadFeedbackFromCache(messageId: string): 'like' | 'dislike' | null {
    if (typeof window === 'undefined') return null;

    const feedbackKey = `feedback-${messageId}-${this.userId}`;
    const stored = localStorage.getItem(feedbackKey);

    if (stored === 'like' || stored === 'dislike') {
      return stored;
    }

    return null;
  }

  private extractMessageFileIds(
    msg: DifyHistoryMessage,
    owner: 'user' | 'assistant'
  ): string[] {
    const files = msg.message_files;
    if (!Array.isArray(files)) {
      return [];
    }

    return files
      .map(file => this.asDifyMessageFile(file))
      .filter((file): file is DifyMessageFile => Boolean(file))
      .filter((file): file is DifyMessageFileWithId => (
        file.belongs_to === owner && typeof file.id === 'string'
      ))
      .map(file => file.id);
  }

  private asDifyMessageFile(value: unknown): DifyMessageFile | null {
    if (!value || typeof value !== 'object') {
      return null;
    }
    return value as DifyMessageFile;
  }
}
